/**
 * Which app shell and build an error came from, for Honeybadger triage.
 *
 * The same web app runs in a desktop/mobile browser, the legacy MobiLoud
 * wrapper, and the Capacitor iOS/Android apps. Without this, app errors are
 * mixed in with browser errors and there is no way to tell which native build
 * (e.g. 20260928) a report came from.
 *
 * Platform and shell are known synchronously. The native version/build come
 * from @capacitor/app's App.getInfo(), which is async, so loadNativeAppInfo()
 * is kicked off at boot and the result is cached; notices fired before it
 * resolves simply omit app_version/app_build. Everything here is best-effort
 * and never throws.
 *
 * @capacitor/app is loaded lazily (dynamic import) so it is only fetched inside
 * the Capacitor shell, never in MobiLoud or plain web.
 */

import { isCapacitorEnvironment, capacitorPlatform } from "./detection";
import { isMobiLoudEnvironment } from "../mobiloud/push_registration";

let nativeAppInfo = null;
let loading = false;

/**
 * Fetch and cache the native app version/build. No-op off the Capacitor shell,
 * once loaded, or while a load is in flight; never throws.
 */
export function loadNativeAppInfo() {
  if (!isCapacitorEnvironment() || nativeAppInfo || loading) {
    return;
  }
  loading = true;
  import("@capacitor/app")
    .then(({ App }) => App.getInfo())
    .then((info) => {
      nativeAppInfo = {
        version: info?.version || null,
        build: info?.build || null,
      };
    })
    .catch(() => {
      // Not reported: a missing build tag must never create notices of its own.
    })
    .finally(() => {
      loading = false;
    });
}

function mobileOsFromUserAgent() {
  const userAgent =
    typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  return "unknown";
}

/**
 * @returns {{app_shell: string, app_platform: string, app_version?: string, app_build?: string}}
 */
export function appShellErrorContext() {
  try {
    if (isCapacitorEnvironment()) {
      const context = {
        app_shell: "capacitor",
        app_platform: capacitorPlatform() || "unknown",
      };
      if (nativeAppInfo?.version) context.app_version = nativeAppInfo.version;
      if (nativeAppInfo?.build) context.app_build = nativeAppInfo.build;
      return context;
    }
    if (isMobiLoudEnvironment()) {
      return { app_shell: "mobiloud", app_platform: mobileOsFromUserAgent() };
    }
  } catch {
    // fall through to web
  }
  return { app_shell: "web", app_platform: "web" };
}

/**
 * Honeybadger tags for the context above, e.g.
 * ["shell:capacitor", "platform:ios", "app_build:20260928"].
 *
 * @param {ReturnType<typeof appShellErrorContext>} context
 * @returns {string[]}
 */
export function appShellErrorTags(context) {
  const tags = [
    `shell:${context.app_shell}`,
    `platform:${context.app_platform}`,
  ];
  if (context.app_build) tags.push(`app_build:${context.app_build}`);
  return tags;
}

// Test-only: reset the cached native info between specs.
export function resetNativeAppInfoForTests() {
  nativeAppInfo = null;
  loading = false;
}
